import Vue from 'vue'
import { pick } from 'lodash-es'
import configs from '@/config'
import Sentry, { logInfo } from '@/3rd-parties/sentry'
import { decryptAndParse } from '@/utils/decrypt.util'
import { stringToNumber } from '@/utils/string.utils'

const THIRD_PARTY_INTEGRATION_BASE_URL = configs.thirdPartyIntegrationBaseUrl
const REPORT_API_BASE_URL = configs.reportApiBaseUrl
const AUDIENCE_LOG_FIELDS = ['audienceId', 'presentationId', 'participantId']

let resource = null

const actions = {
  createOrUpdateAudience: {
    method: 'POST',
    url: `${configs.baseUrl}/api/audiences/`,
  },
  createOrUpdateAudienceWithCookie: {
    method: 'POST',
    url: '/api/audiences/',
  },
  // The url naming might confused at the first glance
  // but it's just for setting cookie to the participant
  // The reason I name it to V2 is to hide the fact that we are tracking the audience
  // by using the cookie, we pretends this one is essential cookie while actually it's not
  createOrUpdateAudienceV2: {
    method: 'POST',
    // Solution: submit the request to same domain using root-relative URL
    // And proxy the request to our server
    // Request ---> Whitelabel domain Proxy (guest domain, not owned by us) ---> whitelabel.ahaslides.com ---> Our Backend Server
    // This is called root-relative URL
    url: '/api/audiences/v2',
    // Remove because we are using 1st party cookies (always submit along with the request by default)
    // credentials: true,
  },
  checkParticipantByCookie: {
    method: 'POST',
    url: '/api/audiences/validate',
  },
  getAudienceData: {
    method: 'GET',
    url: `${configs.baseUrl}/api/audiences/audience-data`,
  },
  getAudienceDataCDN: {
    method: 'GET',
    url: `${configs.baseUrl}/api/presentation/audience-data/{uniqueAccessCode}`,
  },
  audienceJoinPresentation: {
    method: 'POST',
    url: configs.api.joinPresentation,
  },
  pushDeviceAssociations: {
    method: 'POST',
    url: `${configs.baseUrl}/api/associate-past-audienceids/{participantDeviceID}`,
  },
  pushCookieAssociations: {
    method: 'POST',
    url: '/api/associate-past-audienceids',
  },
  associateLtiParticipant: {
    method: 'POST',
    url: `${THIRD_PARTY_INTEGRATION_BASE_URL}/lti/associate-participant`,
  },
  getPresentationGroup: {
    method: 'GET',
    url: `${REPORT_API_BASE_URL}/presentation-groups/{presentationId}`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const createOrUpdateAudienceWithCookie = async (payload) => {
  logInfo('create/update audience', pick(payload, AUDIENCE_LOG_FIELDS))
  const res = await resource.createOrUpdateAudienceWithCookie(payload)
  return res.body
}

const createOrUpdateAudience = async (payload) => {
  logInfo('create/update audience', pick(payload, AUDIENCE_LOG_FIELDS))
  const res = await resource.createOrUpdateAudience(payload)
  return res.body
}

const createOrUpdateAudienceV2 = async (payload) => {
  logInfo('create/update audience v2', pick(payload, AUDIENCE_LOG_FIELDS))
  const res = await resource.createOrUpdateAudienceV2(payload)
  return res.body
}

const checkParticipantByCookie = async (payload) => {
  logInfo('checkParticipantByCookie', payload)
  const res = await resource.checkParticipantByCookie(payload)
  return res.body
}

const getAudienceData = async (payload) => {
  const res = await resource.getAudienceData(payload)
  return res.body
}

const getAudienceDataCDN = async (uniqueAccessCode, payload = null, encrypted = false) => {
  const resp = await resource.getAudienceDataCDN({
    uniqueAccessCode,
    ...(encrypted ? { encrypted } : {}),
  }, payload)
  if (encrypted) {
    try {
      return {
        ...resp,
        body: decryptAndParse(resp.body.encrypted, stringToNumber(uniqueAccessCode)),
      }
    } catch (err) {
      // return original response if any exception occurs
      Sentry.captureException(err)
    }
  }
  return resp
}

const audienceJoinPresentation = async (payload) => {
  const res = await resource.audienceJoinPresentation(payload)
  return res.body
}

const pushDeviceAssociations = async (participantDeviceID, associations) => {
  const res = await resource.pushDeviceAssociations({ participantDeviceID }, { associations })
  return res.body
}

const pushCookieAssociations = async (associations) => {
  const res = await resource.pushCookieAssociations({ associations })
  return res.body
}

const associateLtiParticipant = async ({ ltiToken, participantId }) => {
  const res = await resource.associateLtiParticipant({}, { ltiToken, participantId })
  return res.body
}

const getPresentationGroup = async (presentationId) => {
  const res = await resource.getPresentationGroup({ presentationId })
  return res.body
}

export default {
  initResource,
  createOrUpdateAudience,
  createOrUpdateAudienceWithCookie,
  getAudienceData,
  getAudienceDataCDN,
  audienceJoinPresentation,
  pushDeviceAssociations,
  createOrUpdateAudienceV2,
  checkParticipantByCookie,
  pushCookieAssociations,
  associateLtiParticipant,
  getPresentationGroup,
}
